// frontend/src/screens/admin/AuditLogScreen.jsx
import React, { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, TextField, FormControl, InputLabel,
  Select, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Collapse, CircularProgress,
  Alert, Tooltip, Button, Pagination, Divider, Grid,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon           from '@mui/icons-material/Refresh';
import PrintIcon             from '@mui/icons-material/Print';
import HistoryIcon           from '@mui/icons-material/History';
import FilterListIcon        from '@mui/icons-material/FilterList';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';

import {
  useGetShiftAuditLogsQuery,
  useGetAuditPerformersQuery,
} from '../../slices/auditLogApiSlice';
import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';
import { useSelector } from 'react-redux';

// ─── Palette (matches SchedulingDashboard) ──────────────────────────────────
const NAVY  = '#0d1b2a';
const TEAL  = '#0a8577';
const TEAL_L= '#0cb89e';
const AMBER = '#e8a020';
const RED   = '#c0392b';
const GREEN = '#27ae60';
const SLATE = '#1e2d3d';
const MUTED = '#8fa3b1';
const WHITE = '#f0f4f8';

// ─── Action colour map ───────────────────────────────────────────────────────
const ACTION_COLORS = {
  CREATE:        GREEN,
  UPDATE:        TEAL_L,
  DELETE:        RED,
  ASSIGN:        AMBER,
  UNASSIGN:      `${AMBER}99`,
  APPROVE:       GREEN,
  REJECT:        RED,
  CANCEL:        `${RED}99`,
  STATUS_CHANGE: '#9b59b6',
};

const ACTION_LABELS = [
  'CREATE','UPDATE','DELETE','ASSIGN',
  'UNASSIGN','APPROVE','REJECT','CANCEL','STATUS_CHANGE',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fieldLabel = (field) =>
  field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

// ─── Sub-components ──────────────────────────────────────────────────────────

const FilterSelect = ({ label, value, onChange, options }) => (
  <FormControl size="small" sx={{ minWidth: 160,
    '& .MuiOutlinedInput-root': {
      bgcolor: SLATE, color: WHITE, borderRadius: 1.5,
      '& fieldset': { borderColor: `${TEAL}44` },
      '&:hover fieldset': { borderColor: TEAL },
      '&.Mui-focused fieldset': { borderColor: TEAL },
    },
    '& .MuiInputLabel-root': { color: MUTED },
    '& .MuiSvgIcon-root': { color: MUTED },
  }}>
    <InputLabel sx={{ color: MUTED, fontSize: '0.8rem' }}>{label}</InputLabel>
    <Select value={value} label={label} onChange={e => onChange(e.target.value)}
      sx={{ fontSize: '0.8rem', fontFamily: '"DM Mono", monospace' }}
      MenuProps={{
        PaperProps: {
          sx: {
            bgcolor: SLATE,
            border: `1px solid ${TEAL}44`,
            '& .MuiMenuItem-root': {
              color: WHITE,
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.8rem',
              '&:hover': {
                bgcolor: `${TEAL}44`,
                color: WHITE,
              },
              '&.Mui-selected': {
                bgcolor: `${TEAL}33`,
                color: TEAL_L,
                '&:hover': {
                  bgcolor: `${TEAL}55`,
                },
              },
            },
          },
        },
      }}
    >
      <MenuItem value="">
        <em style={{ color: MUTED }}>All</em>
      </MenuItem>
      {options.map(o => (
        <MenuItem key={o} value={o}>{o}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

// ─── Expandable row ──────────────────────────────────────────────────────────
const AuditRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const color = ACTION_COLORS[log.action] || MUTED;

  const ward = log.snapshot?.wardDepartmentArea || '—';
  const shiftDate = log.snapshot?.startDateTime
    ? new Date(log.snapshot.startDateTime).toLocaleDateString()
    : '—';

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: `${TEAL}11` },
          borderLeft: `3px solid ${color}`,
        }}
        onClick={() => setOpen(!open)}
      >
        {/* Expand toggle */}
        <TableCell sx={{ color: MUTED, width: 40, p: 1 }}>
          <IconButton size="small" sx={{ color: MUTED }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Timestamp */}
        <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono", monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {fmtDate(log.createdAt)}
        </TableCell>

        {/* Action */}
        <TableCell>
          <Chip
            label={log.action}
            size="small"
            sx={{
              bgcolor: `${color}22`,
              color,
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.68rem',
              fontWeight: 700,
              border: `1px solid ${color}44`,
            }}
          />
        </TableCell>

        {/* Ward */}
        <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono", monospace', fontSize: '0.75rem' }}>
          {ward}
        </TableCell>

        {/* Shift date */}
        <TableCell sx={{ color: MUTED, fontFamily: '"DM Mono", monospace', fontSize: '0.75rem' }}>
          {shiftDate}
        </TableCell>

        {/* Shift time */}
        <TableCell sx={{ color: MUTED, fontFamily: '"DM Mono", monospace', fontSize: '0.75rem' }}>
          {log.snapshot?.startDateTime
            ? new Date(log.snapshot.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '—'}
          {' → '}
          {log.snapshot?.endDateTime
            ? new Date(log.snapshot.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '—'}
        </TableCell>

        {/* Fields changed */}
        <TableCell sx={{ color: MUTED, fontFamily: '"DM Mono", monospace', fontSize: '0.72rem' }}>
          {log.changes?.length > 0
            ? `${log.changes.length} field${log.changes.length > 1 ? 's' : ''}`
            : log.action === 'CREATE' ? <em>New record</em> : '—'}
        </TableCell>

        {/* Performed by */}
        <TableCell sx={{ color: TEAL_L, fontFamily: '"DM Mono", monospace', fontSize: '0.72rem' }}>
          {log.performedByName || '—'}
        </TableCell>
      </TableRow>

      {/* ── Expanded detail ─────────────────────────────────────────────── */}
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderBottom: 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: `${NAVY}cc`, p: 2.5, borderBottom: `1px solid ${TEAL}22` }}>
              <Grid container spacing={3}>

                {/* Changes */}
                <Grid item xs={12} md={7}>
                  <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5,
                    textTransform: 'uppercase', color: MUTED,
                    fontFamily: '"DM Mono", monospace', mb: 1.5 }}>
                    Field Changes
                  </Typography>
                  {log.changes?.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Field', 'Before', 'After'].map(h => (
                            <TableCell key={h} sx={{ color: MUTED, fontFamily: '"DM Mono", monospace',
                              fontSize: '0.68rem', textTransform: 'uppercase',
                              letterSpacing: 1, borderColor: `${TEAL}22`, pb: 0.5 }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {log.changes.map((c, i) => (
                          <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ color: TEAL_L, fontFamily: '"DM Mono", monospace',
                              fontSize: '0.72rem', borderColor: `${TEAL}11` }}>
                              {fieldLabel(c.field)}
                            </TableCell>
                            <TableCell sx={{ color: `${RED}cc`, fontFamily: '"DM Mono", monospace',
                              fontSize: '0.72rem', borderColor: `${TEAL}11`,
                              maxWidth: 180, wordBreak: 'break-word' }}>
                              {c.oldValue === null || c.oldValue === undefined
                                ? <em style={{ color: MUTED }}>null</em>
                                : String(c.oldValue)}
                            </TableCell>
                            <TableCell sx={{ color: GREEN, fontFamily: '"DM Mono", monospace',
                              fontSize: '0.72rem', borderColor: `${TEAL}11`,
                              maxWidth: 180, wordBreak: 'break-word' }}>
                              {c.newValue === null || c.newValue === undefined
                                ? <em style={{ color: MUTED }}>null</em>
                                : String(c.newValue)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography sx={{ color: MUTED, fontSize: '0.75rem',
                      fontFamily: '"DM Mono", monospace' }}>
                      {log.action === 'CREATE'
                        ? 'Full record snapshot captured below.'
                        : 'No field-level changes recorded.'}
                    </Typography>
                  )}
                </Grid>

                {/* Meta */}
                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5,
                    textTransform: 'uppercase', color: MUTED,
                    fontFamily: '"DM Mono", monospace', mb: 1.5 }}>
                    Entry Details
                  </Typography>
                  {[
                    ['Action',     log.action],
                    ['Shift ID',   log.entityId],
                    ['Ward',       log.snapshot?.wardDepartmentArea],
                    ['Category',   log.snapshot?.categoryOfStaff],
                    ['Line Status',log.snapshot?.lineStatus],
                    ['Performed by', `${log.performedByName || '—'} (${log.performedByEmail || '—'})`],
                    ['Timestamp',  fmtDate(log.createdAt)],
                    ['IP Address', log.ipAddress || '—'],
                    ['Reason',     log.reason || '—'],
                  ].map(([label, val]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between',
                      mb: 0.75, gap: 2 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: MUTED,
                        fontFamily: '"DM Mono", monospace', flexShrink: 0 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: WHITE,
                        fontFamily: '"DM Mono", monospace', textAlign: 'right',
                        wordBreak: 'break-all' }}>
                        {val || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Grid>

              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Main Screen ─────────────────────────────────────────────────────────────
const AuditLogScreen = () => {
  const { userInfo } = useSelector(s => s.auth);

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];

  // ── Filter state ──────────────────────────────────────────────────────────
  const [dateFrom,    setDateFrom]    = useState(firstOfMonth);
  const [dateTo,      setDateTo]      = useState(today);
  const [fAction,     setFAction]     = useState('');
  const [fWard,       setFWard]       = useState('');
  const [fPerformer,  setFPerformer]  = useState('');
  const [fShiftId,    setFShiftId]    = useState('');
  const [page,        setPage]        = useState(1);

  // Build query params
  const queryParams = {
    ...(dateFrom    && { startDate:   dateFrom }),
    ...(dateTo      && { endDate:     dateTo }),
    ...(fAction     && { action:      fAction }),
    ...(fWard       && { ward:        fWard }),
    ...(fPerformer  && { performedBy: fPerformer }),
    ...(fShiftId.trim() && { entityId: fShiftId.trim() }),
    page,
    limit: 50,
  };

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetShiftAuditLogsQuery(queryParams);

  const { data: performers = [] } = useGetAuditPerformersQuery();
  const { data: locations  = [] } = useGetHospitalLocationsQuery();

  const wards = useMemo(() =>
    [...new Set(locations.map(l => l.wardDepartmentArea).filter(Boolean))].sort(),
    [locations]
  );

  const logs  = data?.logs  || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const hasFilters = fAction || fWard || fPerformer || fShiftId;

  const clearFilters = () => {
    setFAction(''); setFWard(''); setFPerformer(''); setFShiftId('');
    setPage(1);
  };

  // ── Summary stats ─────────────────────────────────────────────────────────
  const actionCounts = useMemo(() =>
    logs.reduce((acc, l) => { acc[l.action] = (acc[l.action] || 0) + 1; return acc; }, {}),
    [logs]
  );

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rows = logs.map(l => `
      <tr>
        <td>${fmtDate(l.createdAt)}</td>
        <td>${l.action}</td>
        <td>${l.snapshot?.wardDepartmentArea || '—'}</td>
        <td>${l.snapshot?.startDateTime ? new Date(l.snapshot.startDateTime).toLocaleDateString() : '—'}</td>
        <td>${l.performedByName || '—'}</td>
        <td>${l.changes?.map(c => fieldLabel(c.field)).join(', ') || (l.action === 'CREATE' ? 'New record' : '—')}</td>
      </tr>`).join('');

    w.document.write(`<!DOCTYPE html><html><head><title>Shift Audit Log</title>
      <style>
        body{font-family:'Courier New',monospace;font-size:10px;color:#111;}
        h1{font-size:16px;margin-bottom:4px;}
        p{font-size:9px;color:#555;margin-bottom:12px;}
        table{width:100%;border-collapse:collapse;}
        th{background:#0d1b2a;color:#f0f4f8;padding:6px 8px;text-align:left;font-size:9px;text-transform:uppercase;}
        td{padding:5px 8px;border-bottom:1px solid #e0e0e0;vertical-align:top;}
        tr:nth-child(even) td{background:#f8f8f8;}
        @page{size:A4 landscape;margin:12mm;}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
      </style></head><body>
      <h1>Shift Audit Log</h1>
      <p>Generated ${new Date().toLocaleString()} by ${userInfo?.name || 'Admin'} · Showing ${logs.length} of ${total} records · Period: ${dateFrom} → ${dateTo}</p>
      <table>
        <thead><tr><th>Timestamp</th><th>Action</th><th>Ward</th><th>Shift Date</th><th>Performed By</th><th>Fields Changed</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=()=>{setTimeout(()=>{window.print();setTimeout(()=>window.close(),200)},400)}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: NAVY, p: { xs: 2, md: 3 },
      fontFamily: '"DM Mono","Courier New",monospace',
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');`}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <HistoryIcon sx={{ color: TEAL, fontSize: 28 }} />
            <Typography sx={{ fontFamily: '"DM Mono",monospace',
              fontSize: { xs: '1.2rem', md: '1.6rem' },
              fontWeight: 700, color: WHITE, letterSpacing: 1 }}>
              Shift Audit Log
            </Typography>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: '0.72rem',
            fontFamily: '"DM Mono",monospace', letterSpacing: 1.5 }}>
            BEAUMONT HOSPITAL · COMPLETE CHANGE HISTORY
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip label={`${total} records`} size="small"
            sx={{ bgcolor: `${TEAL}22`, color: TEAL_L,
              fontFamily: '"DM Mono",monospace', fontSize: '0.7rem',
              border: `1px solid ${TEAL}44` }} />
          <Tooltip title="Print current page">
            <Button onClick={handlePrint} size="small"
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: `${TEAL}22`, color: TEAL_L,
                border: `1px solid ${TEAL}44`,
                fontFamily: '"DM Mono",monospace', fontSize: '0.72rem',
                letterSpacing: 1, textTransform: 'uppercase', px: 1.5,
                '&:hover': { bgcolor: TEAL, color: WHITE } }}>
              Print
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={refetch} size="small"
              sx={{ color: MUTED, '&:hover': { color: TEAL_L, bgcolor: `${TEAL}22` } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`,
        borderRadius: 2, p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterListIcon sx={{ color: MUTED, fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.68rem', letterSpacing: 2,
            textTransform: 'uppercase', color: MUTED,
            fontFamily: '"DM Mono",monospace' }}>
            Filters
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="From" type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true, style: { color: MUTED } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE,
              '& fieldset': { borderColor: `${TEAL}44` } },
              '& input': { fontFamily: '"DM Mono",monospace', fontSize: '0.8rem' } }} />

          <TextField size="small" label="To" type="date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true, style: { color: MUTED } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE,
              '& fieldset': { borderColor: `${TEAL}44` } },
              '& input': { fontFamily: '"DM Mono",monospace', fontSize: '0.8rem' } }} />

          <FilterSelect label="Action Type" value={fAction}
            onChange={v => { setFAction(v); setPage(1); }}
            options={ACTION_LABELS} />

          <FilterSelect label="Ward / Dept" value={fWard}
            onChange={v => { setFWard(v); setPage(1); }}
            options={wards} />

          <FilterSelect label="Performed By" value={fPerformer}
            onChange={v => { setFPerformer(v); setPage(1); }}
            options={performers} />

          <TextField size="small" label="Shift ID" value={fShiftId}
            onChange={e => { setFShiftId(e.target.value); setPage(1); }}
            placeholder="Paste shift _id…"
            sx={{ minWidth: 200,
              '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE,
                '& fieldset': { borderColor: `${TEAL}44` },
                '&:hover fieldset': { borderColor: TEAL } },
              '& .MuiInputLabel-root': { color: MUTED },
              '& input': { fontFamily: '"DM Mono",monospace', fontSize: '0.78rem' } }} />

          {hasFilters && (
            <Button size="small" onClick={clearFilters}
              sx={{ color: MUTED, fontFamily: '"DM Mono",monospace',
                fontSize: '0.7rem', '&:hover': { color: WHITE } }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* ── Action summary chips ─────────────────────────────────────────── */}
      {Object.keys(actionCounts).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {Object.entries(actionCounts).map(([action, count]) => (
            <Chip key={action} size="small"
              label={`${action}: ${count}`}
              onClick={() => { setFAction(action); setPage(1); }}
              sx={{
                bgcolor: `${ACTION_COLORS[action] || MUTED}22`,
                color:    ACTION_COLORS[action] || MUTED,
                border:  `1px solid ${ACTION_COLORS[action] || MUTED}44`,
                fontFamily: '"DM Mono",monospace',
                fontSize: '0.68rem', cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }} />
          ))}
        </Box>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center',
          alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ bgcolor: `${RED}22`, color: RED,
          border: `1px solid ${RED}44` }}>
          Failed to load audit logs. Please try again.
        </Alert>
      ) : logs.length === 0 ? (
        <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`,
          borderRadius: 2, p: 6, textAlign: 'center' }}>
          <HistoryIcon sx={{ color: MUTED, fontSize: 48, mb: 2 }} />
          <Typography sx={{ color: MUTED, fontFamily: '"DM Mono",monospace' }}>
            No audit records found for the selected filters.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`,
          borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['', 'Timestamp', 'Action', 'Ward / Dept', 'Shift Date',
                    'Shift Time', 'Changes', 'Performed By'].map(h => (
                    <TableCell key={h} sx={{
                      bgcolor: NAVY, color: MUTED,
                      fontFamily: '"DM Mono",monospace',
                      fontSize: '0.68rem', textTransform: 'uppercase',
                      letterSpacing: 1, borderColor: `${TEAL}22`,
                      whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <AuditRow key={log._id} log={log} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', p: 2, borderTop: `1px solid ${TEAL}22` }}>
            <Typography sx={{ fontSize: '0.72rem', color: MUTED,
              fontFamily: '"DM Mono",monospace' }}>
              Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total} records
            </Typography>
            <Pagination
              count={pages} page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: MUTED, fontFamily: '"DM Mono",monospace',
                  '&.Mui-selected': { bgcolor: TEAL, color: WHITE },
                  '&:hover': { bgcolor: `${TEAL}22` },
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* ── Hint ─────────────────────────────────────────────────────────── */}
      <Box sx={{ textAlign: 'center', pt: 2 }}>
        <Typography sx={{ fontSize: '0.62rem', color: `${MUTED}88`,
          fontFamily: '"DM Mono",monospace', letterSpacing: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
          Click any row to expand full change details · Logs are read-only
        </Typography>
      </Box>
    </Box>
  );
};

export default AuditLogScreen;